import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

const TOKEN_KEY = "bragame_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Prepend backend URL to a server-relative asset path (e.g. "/api/assets/models/model_01/dressed.png")
export const asset = (path) => (path ? `${BACKEND_URL}${path}` : "");

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// 401 handling — only redirect on **authenticated** failures.
//
// Two classes of 401 exist:
//   1. Request had NO `Authorization` header (public-page hits an authed
//      endpoint) → NOT a session expiry. Reject silently; caller decides.
//   2. Request had an `Authorization` header the server rejected
//      (real session expiry: token revoked, expired, or user banned)
//      → hard-redirect to /login?expired=1 so the login page can surface
//      the "your session expired" banner.
//
// The `/auth/me` call is exempt because AuthContext already handles its
// own boot-time 401 (with its own gated redirect).
//
// The safelist below prevents redirect loops on public routes.
const PUBLIC_PATHS = ["/", "/login", "/signup", "/blocked", "/verify-email", "/forgot-password", "/reset-password", "/verify"];
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !url.endsWith("/auth/me")) {
      // Was the request actually authed? If no Authorization header was
      // sent (== no token in storage at request time), this is a public-
      // page 401 — do not redirect and do not touch the token.
      const wasAuthed = !!err?.config?.headers?.Authorization || !!getToken();
      if (wasAuthed) {
        clearToken();
        if (
          typeof window !== "undefined" &&
          !PUBLIC_PATHS.includes(window.location.pathname) &&
          !window.location.pathname.startsWith("/login")
        ) {
          window.location.replace("/login?expired=1");
        }
      }
    }
    return Promise.reject(err);
  }
);

export default api;
